import {
  EditFilled,
  CloseOutlined,
  DoubleRightOutlined,
  MinusSquareFilled,
  RollbackOutlined,
  DoubleLeftOutlined,
} from '@ant-design/icons';
import { Button, Col, Drawer, message, Row, Tabs } from 'antd';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import ActivateApplicantModal from '../../components/ActivateApplicantModal';
import ApplicantAnnotationModal from '../../components/ApplicantAnnotationModal';
import ErrorResult from '../../components/ErrorResult';
import GiveUpApplicantModal from '../../components/GiveUpApplicantModal';
import PageTitle from '../../components/PageTitle';
import PartialMoveCandidateStageModal from '../../components/PartialMoveCandidateStageModal';
import PartialRejectCandidateModal from '../../components/PartialRejectCandidateModal';
import PartialSelectCandidateModal from '../../components/PartialSelectCandidateModal';
import PartialSendAdditionalQuestionsModal from '../../components/PartialSendAdditionalQuestionsModal';
import PartialSendCustomTestsModal from '../../components/PartialSendCustomTestsModal';
import PartialSendEmailModal from '../../components/PartialSendEmailModal';
import PartialSendPracticalTechnicalCaseModal from '../../components/PartialSendPracticalTechnicalCaseModal';
import PartialSendTestsModal from '../../components/PartialSendTestsModal';
import SendSankhyaAdmissionModal from '../../components/SendSankhyaAdmissionModal';
import useCanAccessJobPosting from '../../hooks/useCanAccessJobPosting';
import useSearchCandidatesController from '../../hooks/useSearchCandidatesController';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams, { Optional } from '../../hooks/useTypedParams';
import {
  CandidateCollection,
  CandidateId,
  JobPostingId,
  RecruitmentProcessCollection,
} from '../../types';
import styles from './ApplicantNavigator.module.css';
import ApplicantNavigatorActionsDropdown from './ApplicantNavigatorActionsDropdown';

const ApplicantNavigator = () => {
  const { t } = useTranslation();

  const location = useLocation();
  const navigate = useTypedNavigate();
  const activeTabKey = location.pathname.replace(/\/$/, '').split('/').pop() || 'details';
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenant, id, applicant_id } = useTypedParams({
    tenant: String,
    id: Optional(JobPostingId),
    applicant_id: CandidateId,
  });
  const queryClient = useQueryClient();

  const [changeApplicantsStage, setChangeApplicantStage] = useState(false);
  const [sendEmailApplicants, setSendEmailApplicants] = useState(false);
  const [sendTestsApplicants, setSendTestsApplicants] = useState(false);
  const [sendCustomTestsApplicants, setSendCustomTestsApplicants] = useState(false);
  const [sendAdditionalQuestions, setSendAdditionalQuestions] = useState(false);
  const [rollbackApplicant, setRollbackApplicant] = useState(false);
  const [rejectApplicant, setRejectApplicant] = useState(false);
  const [applicantAnnotation, setApplicantAnnotation] = useState(false);
  const [applicantSelection, setApplicantSelection] = useState(false);
  const [giveUpApplicant, setGiveUpApplicant] = useState(false);
  const [sendSankhyaAdmission, setSendSankhyaAdmission] = useState(false);
  const [sendPracticalTechnicalCase, setSendPracticalTechnicalCase] = useState(false);

  // State to maintain NEXT/PREVIOUS feature when candidate leaves the table (move or reject actions)
  const [overrideCandidateIdx, setOverrideCandidateIdx] = useState<number>(-1);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || '10', 10);

  const jobPosting = useAPIQuery(`/job-postings/${id}/`, { enabled: Boolean(id) });

  const prevSearchCandidates = useSearchCandidatesController({
    page: page - 1,
    jobPostingId: id,
    functionId: jobPosting.data?.function_id || undefined,
    enabled: page >= 1 && (!id || jobPosting.isSuccess),
  });

  const prevTableCandidates = prevSearchCandidates.data;

  const currentSearchCandidates = useSearchCandidatesController({
    jobPostingId: id,
    functionId: jobPosting.data?.function_id || undefined,
    enabled: page >= 1 && (!id || jobPosting.isSuccess),
  });

  const currentTableCandidates = currentSearchCandidates.data;

  const nextSearchCandidates = useSearchCandidatesController({
    jobPostingId: id,
    page: page + 1,
    functionId: jobPosting.data?.function_id || undefined,
    enabled: page >= 1 && (!id || jobPosting.isSuccess) && Boolean(currentTableCandidates?.next),
  });

  const nextTableCandidates = nextSearchCandidates.data;

  const currentCandidateIdx = currentTableCandidates?.results.findIndex(
    (candidate) => candidate.id === applicant_id
  );

  const candidate = useAPIQuery(`/candidates/${applicant_id}/`);

  const recruitmentProcess = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          candidate: applicant_id,
          job_posting: id,
        },
      },
    ],
    {
      enabled: Boolean(id),
      select(data) {
        return data.results[0];
      },
    }
  );

  const questions = useAPIQuery(
    [
      '/questions/',
      {
        params: {
          job_posting_id: id,
        },
      },
    ],
    { enabled: Boolean(id) }
  );

  const hasAnsweredQuestions = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          candidate: applicant_id,
          job_posting: id,
          has_answered_questions: true,
        },
      },
    ],
    { enabled: Boolean(id) }
  );

  const { canManage } = useCanAccessJobPosting({ jobPostingId: id });

  const recruitmentProcessVisualizationMutation = useAPIMutation(
    async (
      { recruitmentProcessId }: { recruitmentProcessId: RecruitmentProcessCollection['id'] },
      api
    ) => {
      await api.post('/recruitment-process-user-visualizations/', {
        recruitment_process: recruitmentProcessId,
      });
    },
    {
      onSuccess: async (_, __) => {
        await queryClient.invalidateQueries([
          '/recruitment-processes/',
          {
            params: {
              candidate: applicant_id,
              job_posting: id,
            },
          },
        ]);
        await queryClient.invalidateQueries(['useSearchCandidates']);
      },
    }
  );

  const candidateUserVisualizationMutation = useAPIMutation(
    async ({ candidateId }: { candidateId: CandidateCollection['id'] }, api) => {
      await api.post('/candidate-user-visualizations/', {
        candidate: candidateId,
      });
    },
    {
      onSuccess: async (_, __) => {
        await queryClient.invalidateQueries([`/candidates/${applicant_id}/`]);
        await queryClient.invalidateQueries(['useSearchCandidates']);
      },
    }
  );

  // For recruitment process visualization
  useEffect(() => {
    if (
      !recruitmentProcessVisualizationMutation.isLoading &&
      !recruitmentProcess.isFetching &&
      recruitmentProcess.data?.id &&
      !recruitmentProcess.data?.recruiter_visualization_id &&
      id
    ) {
      recruitmentProcessVisualizationMutation.mutate({
        recruitmentProcessId: recruitmentProcess.data.id,
      });
    }
  }, [
    recruitmentProcess.data?.id,
    recruitmentProcess.data?.recruiter_visualization_id,
    recruitmentProcess.isFetching,
    id,
    recruitmentProcessVisualizationMutation,
  ]);

  // For candidate visualization
  useEffect(() => {
    if (
      !candidateUserVisualizationMutation.isLoading &&
      !candidate.isFetching &&
      candidate.data?.id &&
      !candidate.data?.recruiter_visualization_id &&
      !id
    ) {
      candidateUserVisualizationMutation.mutate({ candidateId: candidate.data.id });
    }
  }, [
    candidate.data?.id,
    candidate.data?.recruiter_visualization_id,
    candidate.isFetching,
    candidateUserVisualizationMutation,
    id,
  ]);

  const config = useAPIQuery('/configs/tenant-configurations/');

  const sankhyaAdmission = useAPIQuery(
    [
      '/sankhya/sankhya-admissions/',
      {
        params: {
          recruitment_process: recruitmentProcess.data?.id,
        },
      },
    ],
    {
      enabled: config.data?.enableSankhyaAdmission && !!recruitmentProcess.data?.id,
      select(data) {
        return recruitmentProcess.data?.id && data.count > 0 ? data.results[0] : null;
      },
      retry: (_, error) => error.response?.status !== 404,
    }
  );

  const isSendSankhyaAdmissionDisabled =
    !!sankhyaAdmission.data && sankhyaAdmission.data?.status !== 'FAILED';

  const isHireStage = recruitmentProcess.data?.stage_type === 'HIRED';

  const candidateWhatsApp = candidate.data?.phone_contacts?.find(
    (phoneContact) => phoneContact.type === 'PERSONAL'
  );

  const onClose = () =>
    id
      ? navigate({
          pathname: '/:tenant/opportunities/:id/analysis/',
          params: { tenant, id },
          search: searchParams,
        })
      : navigate({
          pathname: '/:tenant/talent-pool/',
          params: { tenant },
          search: searchParams,
        });

  const pagesToEnableEdit = id
    ? ['details', 'attachments', 'interviews']
    : ['details', 'attachments'];

  return (
    <>
      {candidate.data && id && (
        <>
          <PartialMoveCandidateStageModal
            open={changeApplicantsStage}
            onCancel={() => setChangeApplicantStage(false)}
            jobPostingId={id}
            candidatesIds={[applicant_id]}
            onSuccess={() => {
              setOverrideCandidateIdx(currentCandidateIdx || 0);
              setChangeApplicantStage(false);
              // Case when candidate was the only one in last page
              if (
                page > 1 &&
                !currentTableCandidates?.next &&
                currentTableCandidates?.results.length === 1
              ) {
                searchParams.set('page', `${page - 1}`);
                setSearchParams(searchParams);

                setOverrideCandidateIdx(prevTableCandidates?.results.length || 0);
              }
            }}
            destroyOnClose
          />
          <GiveUpApplicantModal
            open={giveUpApplicant}
            onCancel={() => setGiveUpApplicant(false)}
            candidate={candidate.data}
            jobPostingId={id}
            onSuccess={() => {
              message.success(t('Candidato marcado como desistente.'));
              setGiveUpApplicant(false);
            }}
            destroyOnClose
          />
          {config.data?.enableSankhyaAdmission && (
            <SendSankhyaAdmissionModal
              open={sendSankhyaAdmission}
              onCancel={() => setSendSankhyaAdmission(false)}
              jobPostingId={id}
              candidateId={applicant_id}
              onSuccess={() => {
                setSendSankhyaAdmission(false);
              }}
              destroyOnClose
            />
          )}
        </>
      )}
      <PartialSelectCandidateModal
        ids={[applicant_id]}
        // TODO: VER SE TRADUZ AQUI
        actionOrigin={id ? 'Triagem entre vagas' : 'Banco de Talentos'}
        open={applicantSelection}
        onCancel={() => setApplicantSelection(false)}
        onSuccess={() => {
          message.success(t('Candidato triado.'));
          setApplicantSelection(false);
        }}
        destroyOnClose
      />
      <ApplicantAnnotationModal
        open={applicantAnnotation}
        fromStage={recruitmentProcess.data?.stage?.name}
        jobPostingId={id}
        onCancel={() => setApplicantAnnotation(false)}
        candidateId={applicant_id}
        onSuccess={() => {
          message.success(t('Comentário registrado.'));
          setApplicantAnnotation(false);
        }}
        destroyOnClose
      />
      <PartialSendEmailModal
        jobPostingId={id}
        candidateIds={[applicant_id]}
        open={sendEmailApplicants}
        onCancel={() => setSendEmailApplicants(false)}
        onSuccess={() => {
          message.success(t('Mensagem enviada.'));
          setSendEmailApplicants(false);
        }}
        destroyOnClose
      />
      <PartialSendTestsModal
        jobPostingId={id}
        candidateIds={[applicant_id]}
        open={sendTestsApplicants}
        width={800}
        onSuccess={() => {
          message.success(t('Testes enviados.'));
          setSendTestsApplicants(false);
        }}
        onCancel={() => setSendTestsApplicants(false)}
        destroyOnClose
      />
      {config.data?.enableAlchemerIntegration && (
        <PartialSendCustomTestsModal
          jobPostingId={id}
          candidateIds={[applicant_id]}
          open={sendCustomTestsApplicants}
          width={800}
          onSuccess={() => {
            message.success(t('Testes customizados enviados.'), 6);
            setSendCustomTestsApplicants(false);
          }}
          onCancel={() => setSendCustomTestsApplicants(false)}
          destroyOnClose
        />
      )}
      <PartialSendAdditionalQuestionsModal
        jobPostingId={id}
        candidateIds={[applicant_id]}
        open={sendAdditionalQuestions}
        onCancel={() => setSendAdditionalQuestions(false)}
        onSuccess={() => {
          message.success(t('Perguntas de pré-requisito enviadas.'));
          setSendAdditionalQuestions(false);
        }}
        destroyOnClose
      />
      <PartialSendPracticalTechnicalCaseModal
        candidateIds={[applicant_id]}
        jobPostingId={id}
        stage={recruitmentProcess.data?.stage}
        open={sendPracticalTechnicalCase}
        width={800}
        onCancel={() => setSendPracticalTechnicalCase(false)}
        onSuccess={() => {
          message.success(t('Cases técnicos ou práticos enviados.'));
          setSendPracticalTechnicalCase(false);
        }}
        destroyOnClose
      />
      {id && (
        <PartialRejectCandidateModal
          okButtonProps={{ htmlType: 'submit', icon: <MinusSquareFilled />, danger: true }}
          okText={t('Reprovar')}
          candidateIds={[applicant_id]}
          jobPostingId={id}
          open={rejectApplicant}
          onCancel={() => setRejectApplicant(false)}
          onSuccess={() => {
            message.success(t('Candidato reprovado.'));
            setOverrideCandidateIdx(currentCandidateIdx || 0);
            setRejectApplicant(false);
            // Case when candidate was the only one in last page
            if (
              page > 1 &&
              !currentTableCandidates?.next &&
              currentTableCandidates?.results.length === 1
            ) {
              searchParams.set('page', `${page - 1}`);
              setSearchParams(searchParams);

              setOverrideCandidateIdx(
                prevTableCandidates ? prevTableCandidates.results.length - 1 : 0
              );
            }
          }}
          destroyOnClose
        />
      )}
      <ActivateApplicantModal
        okText={t('Retornar')}
        okButtonProps={{ htmlType: 'submit', icon: <RollbackOutlined /> }}
        candidateId={applicant_id}
        jobPostingId={id}
        open={rollbackApplicant}
        onCancel={() => setRollbackApplicant(false)}
        onSuccess={() => {
          message.success(t('Candidato retornado.'));
          setOverrideCandidateIdx(currentCandidateIdx || 0);
          setRollbackApplicant(false);
          // Case when candidate was the only one in last page
          if (
            page > 1 &&
            !currentTableCandidates?.next &&
            currentTableCandidates?.results.length === 1
          ) {
            searchParams.set('page', `${page - 1}`);
            setSearchParams(searchParams);

            setOverrideCandidateIdx(prevTableCandidates?.results.length || 0);
          }
        }}
        destroyOnClose
      />
      <Drawer
        open
        size="large"
        maskClosable
        onClose={onClose}
        className={styles.drawer}
        closable={false}
        width={870}
        title={
          <PageTitle
            title={candidate.data?.name || '...'}
            level={2}
            extra={[
              <React.Fragment key="actions">
                {canManage && pagesToEnableEdit.includes(activeTabKey) && (
                  <Button
                    type="text"
                    shape="circle"
                    onClick={() => {
                      switch (activeTabKey) {
                        case 'interviews':
                          if (id) {
                            navigate({
                              pathname:
                                '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/change/interviews/',
                              params: { tenant, id, applicant_id },
                              search: searchParams,
                            });
                          }
                          break;
                        case 'attachments':
                          if (id) {
                            navigate({
                              pathname:
                                '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/change/attachments/',
                              params: { tenant, id, applicant_id },
                              search: searchParams,
                            });
                          } else {
                            navigate({
                              pathname:
                                '/:tenant/talent-pool/candidates/:applicant_id/change/attachments/',
                              params: { tenant, applicant_id },
                              search: searchParams,
                            });
                          }
                          break;
                        case 'details':
                        default:
                          if (id) {
                            navigate({
                              pathname:
                                '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/change/details/',
                              params: { tenant, id, applicant_id },
                              search: searchParams,
                            });
                          } else {
                            navigate({
                              pathname:
                                '/:tenant/talent-pool/candidates/:applicant_id/change/details/',
                              params: { tenant, applicant_id },
                              search: searchParams,
                            });
                          }
                          break;
                      }
                    }}
                  >
                    <EditFilled />
                  </Button>
                )}
              </React.Fragment>,
              <Button key="close" type="text" onClick={onClose} shape="circle">
                <CloseOutlined />
              </Button>,
            ]}
            footer={
              <Tabs
                activeKey={activeTabKey}
                onChange={(key) => {
                  switch (key) {
                    case 'details':
                      if (id) {
                        navigate(
                          {
                            pathname:
                              '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/details/',
                            params: { tenant, id, applicant_id },
                            search: searchParams,
                          },
                          { replace: true }
                        );
                      } else {
                        navigate(
                          {
                            pathname: '/:tenant/talent-pool/candidates/:applicant_id/details/',
                            params: { tenant, applicant_id },
                            search: searchParams,
                          },
                          { replace: true }
                        );
                      }
                      break;
                    case 'attachments':
                      if (id) {
                        navigate(
                          {
                            pathname:
                              '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/attachments/',
                            params: { tenant, id, applicant_id },
                            search: searchParams,
                          },
                          { replace: true }
                        );
                      } else {
                        navigate(
                          {
                            pathname: '/:tenant/talent-pool/candidates/:applicant_id/attachments/',
                            params: { tenant, applicant_id },
                            search: searchParams,
                          },
                          { replace: true }
                        );
                      }
                      break;
                    case 'questionnaires':
                      if (id) {
                        navigate(
                          {
                            pathname:
                              '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/questionnaires/',
                            params: { tenant, id, applicant_id },
                            search: searchParams,
                          },
                          { replace: true }
                        );
                      } else {
                        navigate(
                          {
                            pathname:
                              '/:tenant/talent-pool/candidates/:applicant_id/questionnaires/',
                            params: { tenant, applicant_id },
                            search: searchParams,
                          },
                          { replace: true }
                        );
                      }
                      break;
                    case 'history':
                      if (id) {
                        navigate(
                          {
                            pathname:
                              '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/history/',
                            params: { tenant, id, applicant_id },
                            search: searchParams,
                          },
                          { replace: true }
                        );
                      } else {
                        navigate(
                          {
                            pathname: '/:tenant/talent-pool/candidates/:applicant_id/history/',
                            params: { tenant, applicant_id },
                            search: searchParams,
                          },
                          { replace: true }
                        );
                      }
                      break;
                    case 'tests':
                      if (id) {
                        navigate(
                          {
                            pathname:
                              '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/tests/',
                            params: { tenant, id, applicant_id },
                            search: searchParams,
                          },
                          { replace: true }
                        );
                      } else {
                        navigate(
                          {
                            pathname: '/:tenant/talent-pool/candidates/:applicant_id/tests/',
                            params: { tenant, applicant_id },
                            search: searchParams,
                          },
                          { replace: true }
                        );
                      }
                      break;
                    case 'custom-tests':
                      if (id) {
                        navigate(
                          {
                            pathname:
                              '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/custom-tests/',
                            params: { tenant, id, applicant_id },
                            search: searchParams,
                          },
                          { replace: true }
                        );
                      } else {
                        navigate(
                          {
                            pathname: '/:tenant/talent-pool/candidates/:applicant_id/custom-tests/',
                            params: { tenant, applicant_id },
                            search: searchParams,
                          },
                          { replace: true }
                        );
                      }
                      break;
                    case 'interviews':
                      if (id) {
                        navigate(
                          {
                            pathname:
                              '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/interviews/',
                            params: { tenant, id, applicant_id },
                            search: searchParams,
                          },
                          { replace: true }
                        );
                      } else {
                        navigate(
                          {
                            pathname: '/:tenant/talent-pool/candidates/:applicant_id/interviews/',
                            params: { tenant, applicant_id },
                            search: searchParams,
                          },
                          { replace: true }
                        );
                      }
                      break;
                  }
                }}
                items={[
                  { key: 'details', label: t('Dados') },
                  { key: 'attachments', label: t('Anexos') },
                  { key: 'questionnaires', label: t('Perguntas de pré-requisito') },
                  { key: 'history', label: t('Histórico') },
                  { key: 'tests', label: t('Testes') },
                  ...(config.data?.enableAlchemerIntegration
                    ? [{ key: 'custom-tests', label: t('Testes Customizados') }]
                    : []),
                  { key: 'interviews', label: t('Entrevistas') },
                ]}
              />
            }
          />
        }
        footer={
          <Row gutter={[16, 16]}>
            <Col>
              <ApplicantNavigatorActionsDropdown
                location={id ? 'CANDIDATE_ANALYSIS' : 'TALENT_POOL'}
                moveStage={
                  id &&
                  !isHireStage &&
                  canManage &&
                  jobPosting.data?.status === 'IN_PROGRESS' &&
                  recruitmentProcess.data?.status === 'ACTIVE'
                }
                onMoveStageClick={() => setChangeApplicantStage(true)}
                reject={
                  id && !isHireStage && canManage && recruitmentProcess.data?.status === 'ACTIVE'
                }
                onRejectClick={() => setRejectApplicant(true)}
                rollback={
                  id &&
                  !isHireStage &&
                  canManage &&
                  jobPosting.data?.status === 'IN_PROGRESS' &&
                  recruitmentProcess.data?.status === 'INACTIVE'
                }
                onRollbackClick={() => setRollbackApplicant(true)}
                givenUp={isHireStage && recruitmentProcess.data?.status === 'ACTIVE' && canManage}
                onGivenUpClick={() => setGiveUpApplicant(true)}
                comment
                onCommentClick={() => setApplicantAnnotation(true)}
                select={canManage}
                onSelectClick={() => setApplicantSelection(true)}
                disableSendAdditionalQuestions={!!hasAnsweredQuestions.data?.count}
                sendAdditionalQuestions={Boolean(canManage && id && questions.data?.count)}
                onSendAdditionalQuestionsClick={() => setSendAdditionalQuestions(true)}
                sendCustomTests={canManage && config.data?.enableAlchemerIntegration}
                onSendCustomTestsClick={() => setSendCustomTestsApplicants(true)}
                sendEmail={canManage}
                onSendEmailClick={() => setSendEmailApplicants(true)}
                sendTests={canManage}
                onSendTestsClick={() => setSendTestsApplicants(true)}
                disableSendSankhyaAdmission={isSendSankhyaAdmissionDisabled}
                sendSankhyaAdmission={
                  config.data?.enableSankhyaAdmission &&
                  recruitmentProcess.data?.stage_type === 'HIRED' &&
                  recruitmentProcess.data.status === 'ACTIVE' &&
                  canManage
                }
                onSendSankhyaAdmissionClick={() => setSendSankhyaAdmission(true)}
                sendWhatsApp={canManage}
                candidateWhatsApp={candidateWhatsApp}
                sendPracticalTechnicalCase={
                  recruitmentProcess.data?.stage.sub_type === 'PRACTICAL_TECHNICAL_CASE'
                }
                onSendPracticalTechnicalCaseClick={() => setSendPracticalTechnicalCase(true)}
              />
            </Col>
            <Col flex={1} />
            <Col>
              <Button
                disabled={page - 1 < 1 && (overrideCandidateIdx === 0 || currentCandidateIdx === 0)}
                onClick={() => {
                  if (currentCandidateIdx === undefined) {
                    return;
                  }

                  // Case when candidate is not in table (left by move or reject action)
                  if (currentCandidateIdx === -1) {
                    // Previous candidate is in previous page
                    if (overrideCandidateIdx === 0 && page - 1 >= 1) {
                      searchParams.set('page', `${page - 1}`);
                      searchParams.delete('next');
                      setOverrideCandidateIdx(-1);
                      return navigate({
                        pathname:
                          '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/details/',
                        params: {
                          tenant,
                          id,
                          applicant_id: prevTableCandidates?.results[pageSize - 1].id,
                        },
                        search: searchParams,
                      });
                    }

                    // Previous candidate is in same page
                    if (overrideCandidateIdx > 0 && currentTableCandidates) {
                      const temporaryIdx = overrideCandidateIdx;
                      setOverrideCandidateIdx(-1);
                      return navigate({
                        pathname:
                          '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/details/',
                        params: {
                          tenant,
                          id,
                          applicant_id: currentTableCandidates?.results[temporaryIdx - 1].id,
                        },
                        search: searchParams,
                      });
                    }
                  }

                  // Resetting override value to not influence normal activity
                  setOverrideCandidateIdx(-1);

                  if (currentTableCandidates && currentCandidateIdx - 1 >= 0) {
                    if (id) {
                      return navigate({
                        pathname:
                          '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/details/',
                        params: {
                          tenant,
                          id,
                          applicant_id: currentTableCandidates?.results[currentCandidateIdx - 1].id,
                        },
                        search: searchParams,
                      });
                    } else {
                      return navigate({
                        pathname: '/:tenant/talent-pool/candidates/:applicant_id/details/',
                        params: {
                          tenant,
                          applicant_id: currentTableCandidates?.results[currentCandidateIdx - 1].id,
                        },
                        search: searchParams,
                      });
                    }
                  }

                  if (page - 1 >= 1) {
                    searchParams.set('page', `${page - 1}`);
                    searchParams.delete('next');
                    if (id) {
                      return navigate({
                        pathname:
                          '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/details/',
                        params: {
                          tenant,
                          id,
                          applicant_id: prevTableCandidates?.results[pageSize - 1].id,
                        },
                        search: searchParams,
                      });
                    } else {
                      return navigate({
                        pathname: '/:tenant/talent-pool/candidates/:applicant_id/details/',
                        params: {
                          tenant,
                          applicant_id: prevTableCandidates?.results[pageSize - 1].id,
                        },
                        search: searchParams,
                      });
                    }
                  }
                }}
              >
                <DoubleLeftOutlined /> <Trans>Anterior</Trans>
              </Button>
            </Col>
            <Col>
              <Button
                disabled={
                  currentTableCandidates &&
                  !currentTableCandidates.next &&
                  (currentCandidateIdx === currentTableCandidates?.results.length - 1 ||
                    overrideCandidateIdx === currentTableCandidates?.results.length)
                }
                onClick={() => {
                  if (currentCandidateIdx === undefined) {
                    return;
                  }

                  // Case when candidate is not in table (left by move or reject action)
                  if (currentCandidateIdx === -1) {
                    // Next candidate is the same position as candidate that left
                    if (overrideCandidateIdx >= 0 && currentTableCandidates) {
                      const temporaryIdx = overrideCandidateIdx;
                      setOverrideCandidateIdx(-1);
                      return navigate({
                        pathname:
                          '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/details/',
                        params: {
                          tenant,
                          id,
                          applicant_id: currentTableCandidates?.results[temporaryIdx].id,
                        },
                        search: searchParams,
                      });
                    }
                  }

                  // Resetting override value to not influence normal activity
                  setOverrideCandidateIdx(-1);

                  if (
                    currentTableCandidates &&
                    currentCandidateIdx + 1 < currentTableCandidates.results.length
                  ) {
                    if (id) {
                      return navigate({
                        pathname:
                          '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/details/',
                        params: {
                          tenant,
                          id,
                          applicant_id: currentTableCandidates?.results[currentCandidateIdx + 1].id,
                        },
                        search: searchParams,
                      });
                    } else {
                      return navigate({
                        pathname: '/:tenant/talent-pool/candidates/:applicant_id/details/',
                        params: {
                          tenant,
                          applicant_id: currentTableCandidates?.results[currentCandidateIdx + 1].id,
                        },
                        search: searchParams,
                      });
                    }
                  }

                  if (nextTableCandidates && nextTableCandidates.results.length > 0) {
                    searchParams.set('page', `${page + 1}`);
                    if (id) {
                      return navigate({
                        pathname:
                          '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/details/',
                        params: {
                          tenant,
                          id,
                          applicant_id: nextTableCandidates?.results[0].id,
                        },
                        search: searchParams,
                      });
                    } else {
                      return navigate({
                        pathname: '/:tenant/talent-pool/candidates/:applicant_id/details/',
                        params: {
                          tenant,
                          applicant_id: nextTableCandidates?.results[0].id,
                        },
                        search: searchParams,
                      });
                    }
                  }
                }}
              >
                <Trans>Próximo</Trans> <DoubleRightOutlined />
              </Button>
            </Col>
          </Row>
        }
      >
        <ErrorResult error={candidate.error}>
          <Outlet />
        </ErrorResult>
      </Drawer>
    </>
  );
};

export default ApplicantNavigator;
